import { Fragment } from "react";
import { connectorDirectory } from "../lib/connectorDirectory";
import { playClick } from "../lib/sounds";
import {
  ConnectorDropdown,
  Divider,
  Row,
  SectionLabel,
  SubtleButton,
} from "./ConnectorDropdown";
import { type AppInfo, ConnectorOption, ConnectorRow } from "./ConnectorRow";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { makeUrl, routes } from "../lib/urls";

export type ConnectedItem = AppInfo & {
  manageTo: string;
  onRemove: () => void;
};

export type AvailableItem = AppInfo & {
  onSelect: () => void;
};

export type DisconnectedItem = {
  id: string;
  appType: string;
  slug?: string;
  options: AppInfo[];
  onSelect: (connectedAppId: string) => void;
  onCreateNew: () => void;
};

type Props = {
  opener: React.ReactNode;
  parentNoun: string;
  connected: ConnectedItem[];
  available: AvailableItem[];
  disconnected?: DisconnectedItem[];
  onCreateApp?: (identifier: string) => void;
  margin?: number;
  style?: React.CSSProperties;
  disabled?: boolean;
};

export function ConnectorSelectorDropdown({
  opener,
  parentNoun,
  connected,
  available,
  disconnected = [],
  onCreateApp,
  margin,
  style,
  disabled,
}: Props) {
  return (
    <ConnectorDropdown
      opener={opener}
      margin={margin}
      style={style}
      disabled={disabled}
    >
      {(query, close, shiftHeld) => {
        const lowerQuery = query.toLowerCase().trim();
        const match = (name: string) => name.toLowerCase().includes(lowerQuery);
        const filteredConnected = lowerQuery
          ? connected.filter((a) => match(a.name))
          : connected;
        const filteredAvailable = lowerQuery
          ? available.filter((a) => match(a.name))
          : available;
        const customApp = connectorDirectory.find(
          (a) => a.identifier === "apiKey"
        );
        const directoryResults = lowerQuery
          ? connectorDirectory.filter((a) => match(a.name))
          : [];
        const createItems = !onCreateApp
          ? []
          : directoryResults.length
            ? directoryResults
            : filteredConnected.length === 0 &&
                filteredAvailable.length === 0 &&
                customApp
              ? [customApp]
              : [];

        const sections: { label: string; rows: React.ReactNode }[] = [];

        if (filteredConnected.length) {
          sections.push({
            label: `On this ${parentNoun}`,
            rows: filteredConnected.map((app) => (
              <ConnectorOption
                key={app.id}
                app={app}
                to={app.manageTo}
                close={close}
                hover={
                  <Tooltip content={`Remove from ${parentNoun}`}>
                    <SubtleButton
                      icon="circle-minus"
                      variant="secondary"
                      onClick={app.onRemove}
                    />
                  </Tooltip>
                }
              />
            )),
          });
        }

        if (disconnected.length) {
          sections.push({
            label: "Needs connection",
            rows: disconnected.map((item) => {
              const appDef = connectorDirectory.find(
                (a) => a.identifier === item.appType
              );
              const name = item.slug ?? appDef?.name ?? item.appType;
              return (
                <Row
                  key={item.id}
                  onClick={() => {
                    playClick();
                    item.onCreateNew();
                    close();
                  }}
                >
                  <ConnectorRow
                    template={{ name, identifier: item.appType }}
                    name={name}
                    secondary="Disconnected"
                    grayscale
                  />
                </Row>
              );
            }),
          });
        }

        if (filteredAvailable.length) {
          sections.push({
            label: "In this tenant",
            rows: filteredAvailable.map((app) => (
              <ConnectorOption
                key={app.id}
                app={app}
                onClick={app.onSelect}
                openTo={makeUrl(routes.modal.connector, { id: app.id })}
                shiftHeld={shiftHeld}
                close={close}
              />
            )),
          });
        }

        if (createItems.length) {
          sections.push({
            label: "Add new",
            rows: createItems.map((app) => (
              <Row
                key={app.identifier}
                onClick={() => {
                  playClick();
                  onCreateApp?.(app.identifier);
                  close();
                }}
              >
                <ConnectorRow
                  template={{ name: app.name, identifier: app.identifier }}
                  name={app.name}
                  secondary={app.tagLine}
                />
              </Row>
            )),
          });
        }

        return sections.map((section, i) => (
          <Fragment key={section.label}>
            {i > 0 && <Divider />}
            <SectionLabel>
              <Text variant="text-small" color="var(--text-secondary)">
                {section.label}
              </Text>
            </SectionLabel>
            {section.rows}
          </Fragment>
        ));
      }}
    </ConnectorDropdown>
  );
}
